<script setup lang="ts">
import { onMounted, ref } from 'vue'

import AuthPasswordInput from '@/components/auth/AuthPasswordInput.vue'
import PrevStepBtn from '@/components/frontend/profile/common/PrevStepBtn.vue'

import { authEncryptPassword } from '@/utils/auth/authEncrypt'
import { notifySuccess } from '@/utils/ui/handleNotification'

import * as aggr from '@/api-generator/aggr'
import { feUserProfileAPI } from '@/APIs'
import { ProfileSteps } from '@/constant/user'
import appStore from '@/stores'

import type { FormInstance, FormRules } from 'element-plus'

const feUserProfileStore = appStore.useFEUserProfileStore

const prevStep = ref<ProfileSteps>(ProfileSteps.PROFILE_INFO)

interface UpdatePasswordFormModel {
  currentPassword: string
  newPassword: string
  confirmNewPassword: string
}

const updatePasswordFormRef = ref<null | FormInstance>(null)
const updatePasswordForm = ref<UpdatePasswordFormModel>({
  currentPassword: '',
  newPassword: '',
  confirmNewPassword: ''
})

const validateCurrentPass = (rule: any, value: string, callback: any) => {
  if (value === '' || value.trim() === '') {
    callback(new Error('請輸入舊密碼'))
  } else if (value.length < 6) {
    callback(new Error('舊密碼至少需要六個字元'))
  } else {
    callback()
  }
}

const validateNewPass = (rule: any, value: string, callback: any) => {
  if (value === '' || value.trim() === '') {
    callback(new Error('請輸入新密碼'))
  } else if (value.length < 6) {
    callback(new Error('新密碼至少需要六個字元'))
  } else {
    if (updatePasswordForm.value.confirmNewPassword !== '') {
      if (!updatePasswordFormRef.value) {
        return
      }
      updatePasswordFormRef.value.validateField('confirmNewPassword', () => {})
    }
    callback()
  }
}

const validateConfirmNewPass = (rule: any, value: string, callback: any) => {
  if (value === '' || value.trim() === '') {
    callback(new Error('請再次輸入新密碼'))
  } else if (value.length < 6) {
    callback(new Error('新密碼至少需要六個字元'))
  } else if (value !== updatePasswordForm.value.newPassword) {
    callback(new Error('新密碼不一致'))
  } else {
    callback()
  }
}

const rules = ref<FormRules<UpdatePasswordFormModel>>({
  currentPassword: [{ validator: validateCurrentPass, trigger: ['blur', 'change'] }],
  newPassword: [{ validator: validateNewPass, trigger: ['blur', 'change'] }],
  confirmNewPassword: [{ validator: validateConfirmNewPass, trigger: ['blur', 'change'] }]
})

const isLoading = ref(false)

const handleUpdatePassword = async (reqData: aggr.FEUserProfileUpdatePasswordRq) => {
  isLoading.value = true
  try {
    const { data } = await feUserProfileAPI.updateUserPassword(reqData)
    if (data.status) {
      notifySuccess('密碼變更成功')
      feUserProfileStore.updateCurrentStep(ProfileSteps.PROFILE_INFO)
    } else {
      ElMessage.error(data.message || '密碼變更失敗')
    }
  } catch (error: any) {
    const errorMsg = error.response.data.message ? error.response.data.message : error.message
    console.error(errorMsg)
  } finally {
    isLoading.value = false
  }
}

const submitForm = (formEl: FormInstance | null) => {
  if (!formEl) {
    return
  }
  formEl.validate((valid) => {
    if (valid) {
      // 密碼加密處理
      const authEncryptedCurrentPassword = authEncryptPassword(updatePasswordForm.value.currentPassword)
      const authEncryptedNewPassword = authEncryptPassword(updatePasswordForm.value.newPassword)
      const authEncryptedConfirmNewPassword = authEncryptPassword(updatePasswordForm.value.confirmNewPassword)
      const reqBody = {
        currentPassword: authEncryptedCurrentPassword,
        newPassword: authEncryptedNewPassword,
        confirmNewPassword: authEncryptedConfirmNewPassword
      }
      handleUpdatePassword(reqBody)
    } else {
      console.error('表單驗證失敗')
    }
  })
}

const currentPwdInputRef = ref<InstanceType<typeof AuthPasswordInput> | null>(null)
onMounted(() => {
  currentPwdInputRef.value?.focus()
})
</script>

<template>
  <div class="profile-edit-password-step1">
    <PrevStepBtn :prev-step="prevStep" />
    <div>
      <h3 class="profile-title">變更密碼</h3>
      <el-form
        ref="updatePasswordFormRef"
        :rules="rules"
        :model="updatePasswordForm"
        @keypress.enter.prevent="submitForm(updatePasswordFormRef)"
      >
        <el-form-item class="profile-edit-password-step1-item" prop="currentPassword">
          <label class="profile-label" for="currentPassword">舊密碼</label>
          <AuthPasswordInput ref="currentPwdInputRef" v-model="updatePasswordForm.currentPassword" />
        </el-form-item>
        <el-form-item class="profile-edit-password-step1-item" prop="newPassword">
          <label class="profile-label" for="newPassword">新密碼</label>
          <AuthPasswordInput v-model="updatePasswordForm.newPassword" />
        </el-form-item>
        <el-form-item class="profile-edit-password-step1-item" prop="confirmNewPassword">
          <label class="profile-label" for="confirmNewPassword">再次確認</label>
          <AuthPasswordInput v-model="updatePasswordForm.confirmNewPassword" />
        </el-form-item>
        <div class="profile-steps-btn-wrapper">
          <BaseButton class="w-[120px]" @click="feUserProfileStore.updateCurrentStep(ProfileSteps.PROFILE_INFO)"
            >取消
          </BaseButton>
          <BaseButton
            class="w-[120px] border-app-brand-blue-500 bg-app-brand-blue-500 text-white"
            :loading="isLoading"
            @click="submitForm(updatePasswordFormRef)"
          >
            <span v-if="isLoading">儲存中</span>
            <span v-else>儲存變更</span>
          </BaseButton>
        </div>
      </el-form>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.profile-edit-password-step1 {
  &-item {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  :deep(.el-input.auth-form-input) {
    height: auto;
    font-size: 14px;

    .el-input__wrapper {
      background-color: #fff;
      border-radius: 8px;
    }
  }
}
</style>
